<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-calendar-check" class="q-mb-sm" /> {{ $t('appointments') }}
		</div>

		<q-list class="q-mb-md filter-card">
			<q-collapsible icon="fas fa-filter" :label="$t('filter')" v-model="show_filter">
				<div class="row gutter-sm">
					<div class="col-12 caption">
						{{ $t('status') }}
					</div>

					<div class="col-12">
						<q-checkbox
							v-model="selected_status"
							:label="$t(getStatus(item))"
							:val="item"
							v-for="(item, index) in 8"
							:key="index"
							style="margin-left: 10px; border-radius:5px"
							@input="changeFilter()"
							:class="getStatusClass(item)"
							color="white"
							keep-color
						/>
					</div>
				</div>
			</q-collapsible>
		</q-list>

		<full-calendar
			defaultView="dayGridMonth"
			:plugins="calendarPlugins"
			:weekends="true"
			:events="filteredEvents"
			ref="fullCalendar"
			:header="{
				left: 'today',
				center: 'title',
				right: 'prev next',
			}"
			:locales="locales"
			:datesRender="datesRender"
			@dateClick="dateClicked"
			:weekNumbers="true"
		/>

		<q-modal
			v-model="show_modal_results"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn
						flat
						round
						dense
						@click="show_modal_results = false"
						icon="fas fa-times"
					/>
					<q-toolbar-title>
						{{ $t('appointments') }} - {{ fDate(result_date) }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<q-table
						:data="date_results"
						:columns="columns"
						:pagination.sync="server_pagination"
						@request="requestEvents"
						:loading="loading"
						row-key="id"
						:no-data-label="$t('no_data_avaliable')"
						:no-results-label="$t('no_results_found')"
						:loading-label="$t('loading_data')"
						:rows-per-page-label="$t('items_per_page')"
						:rows-per-page-options="[5, 10, 50, 100]"
						binary-state-sort
						class="q-mt-lg"
					>
						<template slot="top" slot-scope="props">
							<div class="col" />
							<q-btn
								color="secondary"
								icon="fas fa-plus"
								:label="$t('new')"
								@click="
									$router.push({
										name: 'appointment',
										params: { id: 0, date: result_date },
									})
								"
								:disable="!createAppointments"
							/>
						</template>

						<q-tr slot="header" slot-scope="props" :props="props">
							<q-th v-for="col in props.cols" :key="col.name" :props="props">
								{{ $t(col.label) }}
							</q-th>
						</q-tr>

						<q-tr
							style="cursor: pointer;"
							slot="body"
							slot-scope="props"
							:props="props"
							@click.native="goToAppointment(props.row)"
						>
							<q-td auto-width key="presential_event_id" :props="props">
								{{ props.row.presential_event_id }}
							</q-td>

							<q-td key="title" :props="props">
								{{ props.row.title }}
							</q-td>

							<q-td key="code" :props="props">
								{{ props.row.code }}
							</q-td>

							<q-td auto-width key="hr_start" :props="props">
								{{ fHourStart(props.row) }}
							</q-td>

							<q-td auto-width key="hr_end" :props="props">
								{{ fHourEnd(props.row) }}
							</q-td>

							<q-td key="instructors_list" :props="props">
								<q-btn
									color="primary"
									text-color="white"
									:label="
										props.row.instructors_list.length
											? props.row.instructors_list.length
											: 0
									"
									no-ripple
								>
									<q-tooltip v-if="props.row.instructors_list.length">
										<p class="tooltip q-mb-sm">
											{{ $t('instructor') }}:
											<span
												v-for="(instructor, index) in props.row
													.instructors_list"
												:key="index"
											>
												{{ instructor }}
												<span
													v-if="
														props.row.instructors_list.length - 1 >
															index
													"
													>,
												</span>
											</span>
										</p>
									</q-tooltip>
								</q-btn>
							</q-td>

							<q-td key="company_name" :props="props">
								{{ props.row.company_name }}
							</q-td>

							<q-td auto-width key="event_status" :props="props">
								<q-chip
									small
									:icon="getStatusIcon(props.row.event_status)"
									:color="getStatusColorChip(props.row.event_status)"
									class="full-width"
								>
									{{ $t(getStatus(props.row.event_status)) }}
								</q-chip>
							</q-td>

							<q-td auto-width key="actions" :props="props">
								<q-btn
									flat
									round
									color="primary"
									icon="fas fa-pencil-alt"
									@click="
										$router.push({
											name: 'appointment',
											params: { id: props.row.actions, date: result_date },
										})
									"
								/>
							</q-td>
						</q-tr>
					</q-table>
				</div>
			</q-modal-layout>
		</q-modal>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
	i18nOptions: { namespaces: ['custom', 'presential', 'common', 'admin'] },

	data() {
		return {
			events: [],
			calendarPlugins: [dayGridPlugin, interactionPlugin],
			locales: allLocales,
			selected_date: date.formatDate(new Date(), 'YYYY-MM-DD'),
			selected_status: [],
			datesRender: this.monthChanged,
			first_load: false,
			show_filter: false,
			date_results: [],
			show_modal_results: false,
			result_date: '',
			columns: [
				{
					name: 'presential_event_id',
					required: true,
					label: '#',
					align: 'left',
					field: 'presential_event_id',
					sortable: false,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: false,
				},
				{
					name: 'code',
					required: true,
					label: 'code',
					align: 'left',
					field: 'code',
					sortable: false,
				},
				{
					name: 'hr_start',
					required: true,
					label: 'start_time',
					align: 'left',
					field: 'hr_start',
					sortable: false,
				},
				{
					name: 'hr_end',
					required: true,
					label: 'end_time',
					align: 'left',
					field: 'hr_end',
					sortable: false,
				},
				{
					name: 'instructors_list',
					required: true,
					label: 'instructor',
					align: 'left',
					field: 'instructors_list',
					sortable: false,
					show: true,
				},
				{
					name: 'company_name',
					required: true,
					label: 'location',
					align: 'left',
					field: 'company_name',
					sortable: false,
				},
				{
					name: 'event_status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'event_status',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'left',
					field: 'actions',
					sortable: false,
				},
			],
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			},
			loading: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			id_user_language_selected: 'common/id_user_language_selected',
			languages: 'params/languages',
			user_client_module_items: 'common/user_client_module_items',
			id_user_client_selected: 'common/id_user_client_selected',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		filteredEvents() {
			if (this.selected_status.length > 0) {
				return this.events.filter((obj) => {
					return this.selected_status.includes(obj.status)
				})
			} else return this.events
		},

		createAppointments() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => obj.slug === 'request_events')
				if (item) return item.permission
			} else return false
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},
	},

	methods: {
		fetchLists() {
			this.$axios
				.get('/accounts/get_manager_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS_PRESENTIAL', [response.data])
				})
				.catch((e) => {
					console.log(e)
				})
		},

		fetchAppointments() {
			this.$q.loading.show()

			let data = {
				selected_date: this.selected_date,
				status: this.selected_status,
			}

			this.$axios
				.get(
					`/presential/calendar/?${this.$qs.stringify(data, { arrayFormat: 'repeat' })}`,
					this.getHeader
				)
				.then((response) => {
					this.events = response.data.map((e) => {
						return {
							...e,
							start: e.event_date,
							title: `${e.qty} ${this.$t(this.getStatus(e.status))}`,
							backgroundColor: this.getStatusColor(e.status),
							borderColor: this.getStatusColor(e.status),
						}
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
					this.first_load = true
				})
		},

		monthChanged(val) {
			if (this.first_load) {
				this.selected_date = date.formatDate(
					this.$refs.fullCalendar.getApi().getDate(),
					'YYYY-MM-DD'
				)
				this.fetchAppointments()
			}
		},

		requestEvents({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				selected_date: this.result_date,
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				ordering: order_by,
			}

			this.$axios
				.get(`/presential/presential_events/?${this.$qs.stringify(data)}`, this.getHeader)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.date_results = response.data.results.map((e) => {
						return {
							...e,
							instructors_list: e.instructors_list
								? e.instructors_list.map((i) => i.name)
								: [],
							actions: e.presential_event_id,
						}
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		dateClicked(val) {
			this.result_date = val.dateStr
			this.date_results = []
			this.show_modal_results = true

			let pagination = {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			}

			this.requestEvents({ pagination })
		},

		changeFilter() {
			this.fetchAppointments()
		},

		getStatusClass(id) {
			switch (id) {
				case 1:
					return 'bg-light-blue-5 text-white q-pa-sm'
					break
				case 2:
					return 'bg-purple-4 text-white q-pa-sm'
					break
				case 3:
					return 'bg-indigo text-white q-pa-sm'
					break
				case 4:
					return 'bg-grey-7 text-white q-pa-sm'
					break
				case 5:
					return 'bg-yellow-10 text-white q-pa-sm'
					break
				case 6:
					return 'bg-dark text-white q-pa-sm'
					break
				case 7:
					return 'bg-red-7 text-white q-pa-sm'
					break
				case 8:
					return 'bg-green-7 text-white q-pa-sm'
				case 9:
					return 'bg-blue-grey-6 text-white q-pa-sm'
					break
			}
		},

		getStatus(id) {
			switch (id) {
				case 1:
					return 'pre_scheduled'
					break
				case 2:
					return 'scheduled'
					break
				case 3:
					return 'in_progress'
					break
				case 4:
					return 'intermission'
					break
				case 5:
					return 'awaiting_results'
					break
				case 6:
					return 'reproved'
					break
				case 7:
					return 'canceled'
					break
				case 8:
					return 'finished'
					break
			}
		},

		getStatusColor(id) {
			switch (id) {
				case 1:
					return '#29b6f6'
					break
				case 2:
					return '#BA68C8'
					break
				case 3:
					return '#3F51B5'
					break
				case 4:
					return '#757575'
					break
				case 5:
					return '#F57F17'
					break
				case 6:
					return '#424242'
					break
				case 7:
					return '#E53935'
					break
				case 8:
					return '#43A047'
					break
				case 9:
					return '#607D8B'
					break
			}
		},

		getStatusColorChip(id) {
			switch (id) {
				case 1:
					return 'yellow'
					break
				case 2:
					return 'purple-4'
					break
				case 3:
					return 'indigo'
					break
				case 4:
					return 'grey-7'
					break
				case 5:
					return 'yellow-10'
					break
				case 6:
					return 'dark'
					break
				case 7:
					return 'red-7'
					break
				case 8:
					return 'green-7'
					break
			}
		},

		getStatusIcon(id) {
			switch (id) {
				case 1:
					return 'fas fa-history'
					break
				case 2:
					return 'far fa-clock'
					break
				case 3:
					return 'fas fa-play'
					break
				case 4:
					return 'fas fa-pause'
					break
				case 5:
					return 'far fa-question-circle'
					break
				case 6:
					return 'far fa-times-circle'
					break
				case 7:
					return 'fas fa-ban'
					break
				case 8:
					return 'fas fa-check'
					break
			}
		},

		setCalendarLanguage() {
			let calendarApi = this.$refs.fullCalendar.getApi()

			let language = this.languages.find((l) => {
				return l.id === this.id_user_language_selected
			})

			if (language) {
				calendarApi.setOption('locale', language.code)
			} else {
				calendarApi.setOption('locale', 'pt-br')
			}
		},

		fDate(d) {
			if (d) {
				let format = ''

				switch (this.id_user_language_selected) {
					case 1:
					case 3:
						format = 'DD/MM/YYYY'
						break
					default:
						format = 'MM-DD-YYYY'
						break
				}

				return moment(d)
					.tz('Etc/GMT')
					.format(format)
			}
		},

		fHourStart(props) {
			return moment(props.hr_start)
				.tz(props.timezone)
				.format('HH:mm')
		},

		fHourEnd(props) {
			return moment(props.hr_end)
				.tz(props.timezone)
				.format('HH:mm')
		},

		goToAppointment(props) {
			this.$router.push({
				name: 'appointment',
				params: { id: props.actions, date: props.hr_start },
			})
		},
	},

	watch: {
		id_user_language_selected: function(val) {
			this.setCalendarLanguage()
		},
	},

	created() {
		this.fetchLists()
		this.fetchAppointments()
	},

	mounted() {
		this.setCalendarLanguage()
	},
}
</script>

<style lang="stylus">
.fc-day
    &:hover
        cursor: pointer
        background: whitesmoke

.filter-card
    border-radius 4px
    box-shadow 0 1px 2px rgba(0,0,0,0.2), 0 1px 1px rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12)
</style>
